import { useState } from "react";
import { Link } from "react-router-dom";
import { Table } from "../../../../../../components/Functional";
import { formatSalesPeopleNames } from "../../../../../../components/Functional/SalesPeopleSplitEditor/salesPeopleSplitEditorUtils";
import { Button, Modal } from "../../../../../../components/ui";
import { UPDATE_OFFER as updateOfferMutation } from "../../../../../../graphql/mutation/Offer";
import { FETCH_OFFERS_V2 as fetchOffersV2 } from "../../../../../../graphql/query/Offer";
import OfferContactCell from "../../../../../Offer/components/OfferContactCell";
import EditOffer from "../../../../../Offer/pages/List/components/EditOffer";
import SectionHeader from "../../components/SectionHeader";
import Actions from "./components/Actions";

// Offers on the instance's artwork, split by where they are held.
const getOfferGroups = ({ artworkId, instanceId }) => {
  const artworkFilter = {
    _artwork: { id: { operator: "eq", value: artworkId } },
    order: "groupByHold",
  };

  return [
    {
      name: "thisInstanceHolds",
      section: "Holds",
      subtitle: "This Instance",
      emptyMessage: "No holds on this instance",
      showHold: false,
      variables: {
        ...artworkFilter,
        holdId: { operator: "eq", value: instanceId },
      },
    },
    {
      name: "otherInstanceHolds",
      section: "Holds",
      subtitle: "Other Instances",
      emptyMessage: "No holds on other instances",
      showHold: true,
      variables: { ...artworkFilter, excludeHoldId: instanceId },
    },
    {
      name: "artworkOffers",
      section: "Offers on this Artwork",
      emptyMessage: "No offers without a hold",
      showHold: false,
      variables: { ...artworkFilter, holdId: { operator: "isNull" } },
    },
  ];
};

// Groups that share a section sit under one heading, in their given order.
const groupBySection = (groups = []) =>
  groups.reduce(
    (sections, group) => {
      const last = sections[sections.length - 1];
      return last?.title === group.section
        ? [...sections.slice(0, -1), { ...last, groups: [...last.groups, group] }]
        : [...sections, { title: group.section, groups: [group] }];
    },
    [],
  );

// Keeps the previous counts when nothing changed so re-renders settle.
const withGroupCount = (counts, name, count) =>
  counts[name] === count ? counts : { ...counts, [name]: count };

const InstanceOffers = (props = {}) => {
  const { artworkId, instanceId } = props;
  const [groupCounts, setGroupCounts] = useState({});

  const holdHeader = {
    label: "Hold",
    name: "hold.name",
    type: "custom",
    component: props => {
      return (
        <Link
          className="underline"
          to={`/instance/${props?.values?.hold?.id}/details`}
        >
          {props?.values?.hold?.name}
        </Link>
      );
    },
  };

  const getHeaders = ({ showHold }) => [
    { label: "Date", name: "date", type: "date" },
    {
      label: "Contact",
      name: "contact.name",
      type: "custom",
      component: ({ values }) => (
        <OfferContactCell values={values} artworkId={artworkId} />
      ),
    },
    { label: "Status", name: "status.description", type: "label" },
    { label: "Currency", name: "currency.code", type: "label" },
    { label: "Amount", name: "amount", type: "currency" },
    { label: "TBI Amount", name: "tbiAmount", type: "currency" },
    {
      label: "Top Deal",
      name: "priority",
      type: "input",
      inputType: "checkbox",
      singleCheckbox: true,
      options: [{ value: -1 }],
    },
    ...(showHold ? [holdHeader] : []),
    {
      label: "Selling People",
      name: "offerSalesPeople",
      type: "custom",
      component: ({ values }) =>
        formatSalesPeopleNames(values?.offerSalesPeople),
    },
    { label: "Date Closed", name: "dateClosed", type: "date" },
    { label: "Notes", name: "notes", type: "label" },
    {
      label: "",
      name: "action",
      type: "custom",
      component: props => <Actions {...props} artworkId={artworkId} />,
    },
  ];

  const getTableProps = group => ({
    variant: "dynamicHeight",
    dataKey: "offersV2",
    headers: getHeaders(group),
    FETCH_QUERY: fetchOffersV2({
      contact: { name: true, id: true },
      contactId: true,
      currency: {},
      currencyId: true,
      status: {},
      statusId: true,
      hold: { name: true, id: true },
      holdId: true,
      amount: true,
      tbiAmount: true,
      tbiDate: true,
      offerSalesPeople: true,
      date: true,
      holdDate: true,
      dateClosed: true,
      priority: true,
      notes: true,
      projectCode: true,
    }),
    variables: group.variables,
    skip: () => !artworkId || !instanceId,
    UPDATE_MUTATION: updateOfferMutation(),
    hideSinglePagePagination: true,
    showRefresh: false,
    // Empty groups collapse to a one-line message; the table stays mounted
    // so it refetches when an offer moves into the group.
    className: groupCounts[group.name] === 0 ? "hidden" : "",
    onQueryStateChange: ({ count }) =>
      setGroupCounts(counts => withGroupCount(counts, group.name, count ?? 0)),
  });

  const modalProps = {
    body: EditOffer,
    closeOnBackdrop: false,
    offer: { artworkId },
    hideCloseButton: false,
    scale: "lg",
  };

  const offersActions = [
    {
      component: () => {
        return (
          <Modal {...modalProps}>
            <Button label="Add Offer" action="black" />
          </Modal>
        );
      },
    },
  ];

  const sections = groupBySection(getOfferGroups({ artworkId, instanceId }));

  return (
    <div className="w-full">
      {sections.map((section, index) => (
        <div key={section.title} className="mb-4 w-full">
          <SectionHeader
            title={section.title}
            actions={index === 0 ? offersActions : undefined}
          />
          {section.groups.map(group => (
            <div key={group.name} className="mb-2 w-full">
              {group.subtitle && (
                <h3 className="px-4 text-xs font-semibold uppercase tracking-wide text-gray-500 sm:px-6 lg:px-8">
                  {group.subtitle}
                </h3>
              )}
              {groupCounts[group.name] === 0 && (
                <p className="px-4 py-2 text-sm text-gray-500 sm:px-6 lg:px-8">
                  {group.emptyMessage}
                </p>
              )}
              <Table {...getTableProps(group)} />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

export { getOfferGroups, groupBySection, withGroupCount };
export default InstanceOffers;
